/**
 * /guide/reminders — list of all shows the visitor has bookmarked.
 * Dark theme — matches Guide.jsx palette.
 *
 * Reads reminders from localStorage, joins them against the LIVE schedule
 * fetched from the public Program Guide endpoint. Stale reminders (whose
 * program no longer exists on the station) are simply hidden — the user
 * can still see + cancel any that do still exist.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Trash2, BellOff, Bookmark } from "lucide-react";
import { GuideShell, useStationGuide, findProgramByIdInChannels } from "./Guide";

const REMINDERS_STORAGE_KEY = "bf_program_guide_reminders";

function loadReminderIds() {
  try {
    const raw = localStorage.getItem(REMINDERS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

// Dark-theme genre tints — saturated accent bar + muted chip bg
const COLOR_TINTS = {
  news:  { bar: "bg-[#4a9eff]", chipBg: "bg-[#0d1e35]", chipTx: "text-[#4a9eff]", chipBorder: "border-[#1e3050]" },
  music: { bar: "bg-[#3ecf8e]", chipBg: "bg-[#0d2318]", chipTx: "text-[#3ecf8e]", chipBorder: "border-[#1f3a2a]" },
  talk:  { bar: "bg-[#a78bfa]", chipBg: "bg-[#1a1230]", chipTx: "text-[#a78bfa]", chipBorder: "border-[#2c1f4a]" },
  sport: { bar: "bg-[#f59e0b]", chipBg: "bg-[#241a08]", chipTx: "text-[#f59e0b]", chipBorder: "border-[#3a2a10]" },
};

export default function GuideReminders() {
  const [ids, setIds] = useState(loadReminderIds);
  const { channels, loading } = useStationGuide();

  useEffect(() => {
    try { localStorage.setItem(REMINDERS_STORAGE_KEY, JSON.stringify(ids)); } catch { /* blocked */ }
  }, [ids]);

  const programs = ids.map((id) => findProgramByIdInChannels(channels, id)).filter(Boolean);
  const cancel = (id) => setIds((cur) => cur.filter((x) => x !== id));
  const clearAll = () => setIds([]);

  return (
    <GuideShell>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-[20px] sm:text-[22px] font-semibold text-[#c8ccd8] tracking-tight">My Reminders</h1>
          <p className="text-[12px] text-[#4a5068] mt-1">
            {loading
              ? "Loading…"
              : programs.length === 0
                ? "Nothing here yet."
                : `${programs.length} reminder${programs.length === 1 ? "" : "s"} set — we'll ping you 5 minutes before each show.`}
          </p>
        </div>
        {programs.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            data-testid="clear-all-reminders"
            className="text-[12px] text-[#ff6b6b] hover:text-[#ff8c8c] hover:underline inline-flex items-center gap-1.5 transition"
          >
            <BellOff className="h-3.5 w-3.5" /> Clear all
          </button>
        )}
      </div>

      {programs.length === 0 ? (
        <div
          className="bg-[#0a0d14] rounded-[10px] p-10 text-center"
          style={{ border: "0.5px dashed #1e2130" }}
          data-testid="reminders-empty"
        >
          <div className="h-12 w-12 mx-auto rounded-full bg-[#0d1e35] text-[#4a9eff] flex items-center justify-center mb-3">
            <Bookmark className="h-5 w-5" />
          </div>
          <div className="text-[14px] font-medium text-[#c8ccd8]">No reminders set yet.</div>
          <p className="text-[12px] text-[#4a5068] mt-1 max-w-sm mx-auto">
            Browse the program guide and tap <strong className="text-[#c8ccd8]">&ldquo;Remind me&rdquo;</strong> on any upcoming show to save it here.
          </p>
          <Link
            to="/guide"
            className="inline-flex items-center mt-4 px-4 py-2 rounded-md bg-[#1a3a5c] hover:bg-[#1e4d7a] text-[#7ec8ff] border border-[#1e4d7a] text-[12px] font-medium transition"
            data-testid="browse-guide"
          >
            Browse the program guide
          </Link>
        </div>
      ) : (
        <ul className="space-y-2.5" data-testid="reminders-list">
          {programs.map((p) => {
            const tint = COLOR_TINTS[p.color] || COLOR_TINTS.news;
            return (
              <li
                key={p.id}
                className="bg-[#0a0d14] rounded-[10px] px-4 py-3 flex items-center gap-4 group hover:border-[#2a3050] transition"
                style={{ border: "0.5px solid #1e2130" }}
                data-testid={`reminder-${p.id}`}
              >
                <span className={`h-10 w-1 rounded ${tint.bar}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[10px] uppercase font-semibold tracking-widest px-1.5 py-0.5 rounded border ${tint.chipBg} ${tint.chipTx} ${tint.chipBorder}`}>
                      {p.channelName}
                    </span>
                    <span className="text-[10px] text-[#4a5068] uppercase">{p.channelType}</span>
                  </div>
                  <div className="text-[14px] font-semibold text-[#c8ccd8] leading-tight truncate">{p.title}</div>
                  <div className="text-[11px] text-[#4a5068] mt-1 inline-flex items-center gap-1.5">
                    <Clock className="h-3 w-3" />
                    Today · {String(p.start).padStart(2, "0")}:00 GMT · {p.duration}h
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => cancel(p.id)}
                  data-testid={`cancel-reminder-${p.id}`}
                  className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#1e2130] text-[#4a5068] hover:bg-[#2a1a1a] hover:text-[#ff6b6b] hover:border-[#3d1f1f] transition"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Cancel
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </GuideShell>
  );
}
